import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { apiSwaggerClientSide } from '@/shared/api';
import { AdminUserWithCounts } from '@/sharedTypes/Api';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

interface IUserTableView {
  onEdit?: (id: string) => void;
}

interface PaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

const UsersTableView = ({ onEdit }: IUserTableView) => {
  const PAGE_SIZE = 20;
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<AdminUserWithCounts[]>([]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearchQuery(e.target.value);
  }

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiSwaggerClientSide.admin.usersList({
        page,
        pageSize: PAGE_SIZE,
        search: searchQuery.length >=2 ? searchQuery : undefined,
      });
      if(res.data?.success && res.data.data) {
        setUsers(res.data.data.users || []);
        setPagination(res.data.data.pagination as PaginationData || null);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false)
      // Also dispatch event for tests to know when loading is done
      document.dispatchEvent(new CustomEvent('usersTableLoaded'));
    }
  }, [page, searchQuery])

  // For debounced search
  useEffect(() => {
    const debounce = setTimeout (() => {
      fetchUsers();
    }, 300)
    return () => clearTimeout(debounce);
  }, [fetchUsers])

  // Skeleton rows for loading state
  const renderSkeletonRows = () => (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <tr key={`skeleton-${index}`} className="border-t hover:bg-gray-50 transition-colors">
          <td className="px-4 py-3 text-sm">
            <Skeleton className="h-4 w-32" />
          </td>
          <td className="px-4 py-3 text-sm">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-20 rounded" />
              <Skeleton className="h-5 w-24 rounded" />
            </div>
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-8 w-16" />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <div className='flex flex-col h-full gap-4' data-testid="users-table-view">

      {/* Search */}
      <Input
        type="text"
        placeholder={t('adminCatalogue.users.searchUsers')}
        value={searchQuery}
        onChange={handleSearchChange}
        className="w-64"
        data-testid="users-search-input"
      />

      {/* Table */}
      <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700"
              data-testid="users-header-username"
              >{t('adminCatalogue.users.username')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700"
              data-testid="users-header-groups-samples"
              >{t('adminCatalogue.users.groupsAndMusicSheets')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700"
              data-testid="users-header-actions"
              >{t('adminCatalogue.users.groupsActions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? renderSkeletonRows() : users.length > 0 ? (
              users.map((user) => (
                <tr key={user.sub} className="border-t hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm" data-testid={`users-username-${user.username}`}>{user.username}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      <span className='px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800'>{user.groupCount || 0} {t('adminCatalogue.users.groups')}</span>
                      <span className='px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800'>{user.privateSampleCount || 0} {t('adminCatalogue.users.musicSheets')}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <ButtonShadcn
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit?.(user.sub!)}
                      data-testid={`users-view-btn-${user.username}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {t('adminCatalogue.common.view')}
                    </ButtonShadcn>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-gray-500">{t('adminCatalogue.users.noUsersFound')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between my-4 px-2">
          <div className="text-sm text-gray-600" data-testid="users-pagination-summary">
            {/* Showing {(page - 1) * PAGE_SIZE + 1} -{' '}
            {Math.min(page * PAGE_SIZE, pagination.total)} of {pagination.total} */}
            {t('adminCatalogue.common.showing', {
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, pagination.total),
              total: pagination.total,
            })}
          </div>
          <div className="flex items-center gap-2">
            <ButtonShadcn
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              data-testid="users-pagination-prev"
            >
              <ChevronLeft className="h-4 w-4" />
              {t('adminCatalogue.common.previous')}
            </ButtonShadcn>
            <span className="text-sm text-gray-600" data-testid="users-pagination-info">
              {/* Page {page} of {pagination.totalPages} */}
              {t('adminCatalogue.common.pageOf', { page, total: pagination.totalPages })}
            </span>
            <ButtonShadcn
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              data-testid="users-pagination-next"
            >
              {t('adminCatalogue.common.next')}
              <ChevronRight className="h-4 w-4" />
            </ButtonShadcn>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersTableView